function isStorageAvailable(storage: Storage): boolean {
  // https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API
  try {
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage.length !== 0
    );
  }
}

class StorageImpl {
  public getItem: (key: string) => string | null;
  public key: (index: number) => string | null;

  public constructor(private _storage: Storage) {
    if (isStorageAvailable(this._storage)) {
      this.getItem = this._storage.getItem.bind(this._storage);
      this.key = this._storage.key.bind(this._storage);
    } else {
      this.getItem = this.key = () => null;
    }
  }

  public get length(): number {
    return isStorageAvailable(this._storage) ? this._storage.length : 0;
  }

  public setItem(key: string, value: string): void {
    if (isStorageAvailable(this._storage)) {
      this._storage.setItem(key, value);
      while (this._storage.getItem(key) !== value) {
        // wait until updated as storage APIs are async
      }
    }
  }

  public removeItem(key: string): void {
    if (isStorageAvailable(this._storage)) {
      this._storage.removeItem(key);
      while (this._storage.getItem(key) !== null) {
        // wait until removed as storage APIs are async
      }
    }
  }

  public clear(): void {
    if (isStorageAvailable(this._storage)) {
      this._storage.clear();
      while (this._storage.length !== 0) {
        // wait until cleared as storage APIs are async
      }
    }
  }
}

export const LocalStorage = new StorageImpl(window.localStorage);
export const SessionStorage = new StorageImpl(window.sessionStorage);
